import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Collapse,
  Chip,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import AuthContext from "connections/auth/AuthProvider.js";
import TextEditor from "components/TextEditor/TextEditor";
import {
  getScenarioPremises,
  updateScenarioPremise,
} from "../../../Cockpit/viewServices/calls/apiCalls";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      width: "90%",
      maxWidth: "900px",
      height: "85vh",
      maxHeight: "700px",
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
    },
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
  },
  headerTitle: {
    fontSize: "1.25rem",
    fontWeight: "500",
    color: theme.palette.secondary.main,
  },
  dialogContent: {
    flex: 1,
    overflowY: "auto",
    padding: "24px",
  },
  premiseCard: {
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    marginBottom: "12px",
    overflow: "hidden",
    transition: "all 0.2s",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
  },
  premiseHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    backgroundColor: "#fafafa",
    gap: "12px",
  },
  premiseHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
  },
  premiseNameKeyGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flexShrink: 0,
  },
  premiseTitle: {
    fontSize: "0.95rem",
    fontWeight: "500",
    color: "#333",
  },
  premiseDescriptionContainer: {
    position: "relative",
    backgroundColor: "#fff",
    border: "1px dashed #ccc",
    borderRadius: "4px",
    minWidth: "300px",
    maxWidth: "550px",
    flex: "1 1 auto",
    minHeight: "44px",
  },
  premiseDescription: {
    fontSize: "0.65rem",
    color: "#555",
    padding: "8px 34px 8px 12px",
    maxHeight: "58px",
    overflow: "hidden",
    lineHeight: "1.5",
    wordBreak: "break-word",
    transition: "max-height 0.2s ease",
    "& p": {
      margin: "0 0 6px 0",
      fontSize: "0.65rem",
      lineHeight: "1.5",
    },
    "& p:last-child": {
      marginBottom: 0,
    },
    "& strong": {
      fontWeight: 600,
    },
    "& em": {
      fontStyle: "italic",
    },
    "& ul, & ol": {
      margin: "4px 0",
      paddingLeft: "18px",
      fontSize: "0.65rem",
    },
    "& li": {
      marginBottom: "3px",
    },
  },
  premiseDescriptionExpanded: {
    maxHeight: "none",
  },
  premiseDescriptionToggle: {
    position: "absolute",
    right: "4px",
    bottom: "4px",
    width: "24px",
    height: "24px",
    padding: 0,
    color: "#666",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      color: (props) => props.clientColor || "#1976d2",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  },
  premiseMetadata: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  metadataChip: {
    height: "24px",
    fontSize: "0.75rem",
  },
  orderChip: {
    height: "24px",
    fontSize: "0.75rem",
    backgroundColor: (props) => props.clientColor || "#1976d2",
    color: "#fff",
    fontWeight: "600",
  },
  setupKeyChipEnabled: {
    height: "24px",
    fontSize: "0.75rem",
    backgroundColor: (props) => props.clientColor || "#1976d2",
    color: "#fff",
    "& .MuiChip-icon": {
      color: "#fff",
      fontSize: "18px",
    },
  },
  setupKeyChipDisabled: {
    height: "24px",
    fontSize: "0.75rem",
    backgroundColor: "#757575",
    color: "#fff",
    "& .MuiChip-icon": {
      color: "#fff",
      fontSize: "18px",
    },
  },
  premiseBody: {
    padding: "16px",
    backgroundColor: "#fff",
    borderTop: "1px solid #e0e0e0",
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  fieldLabel: {
    fontSize: "0.8rem",
    fontWeight: "500",
    color: "#666",
    minWidth: "80px",
    flexShrink: 0,
  },
  readOnlyField: {
    backgroundColor: "#f5f5f5",
    padding: "6px 10px",
    borderRadius: "4px",
    fontSize: "0.8rem",
    color: "#999",
    border: "1px solid #e0e0e0",
    flex: 1,
  },
  descriptionRow: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "12px",
  },
  switchRow: {
    display: "flex",
    gap: "16px",
    marginBottom: "12px",
    "& .MuiSwitch-colorPrimary.Mui-checked": {
      color: (props) => props.clientColor || "#1976d2",
    },
    "& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track": {
      backgroundColor: (props) => props.clientColor || "#1976d2",
    },
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "16px",
    paddingTop: "12px",
    borderTop: "1px solid #e0e0e0",
  },
  cancelButton: {
    color: "#666",
    borderColor: "#ccc",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#999",
    },
  },
  saveButton: {
    backgroundColor: (props) => props.clientColor || "#1976d2",
    color: "#fff",
    "&:hover": {
      backgroundColor: (props) => props.clientColor || "#1565c0",
    },
  },
}));

export default function DialogPremises({ open, onClose }) {
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });
  const { userDetails } = useContext(AuthContext);
  const classes = useStyles({
    clientColor: userDetails?.implementation_color || "#1976d2",
  });

  const [premises, setPremises] = useState([]);
  const [expandedPremise, setExpandedPremise] = useState(null);
  const [expandedDescription, setExpandedDescription] = useState({});
  const [editingPremise, setEditingPremise] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadPremises();
    }
  }, [open]);

  const loadPremises = async () => {
    setIsLoading(true);
    try {
      const data = await getScenarioPremises();
      setPremises(data || []);
    } catch (error) {
      console.error("Error loading premises:", error);
      toast.error(t("premise_update_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (premise) => {
    setExpandedPremise(premise.premise_key);
    setEditingPremise({ ...premise });
  };

  const handleToggleDescription = (premiseKey) => {
    setExpandedDescription((prev) => ({
      ...prev,
      [premiseKey]: !prev[premiseKey],
    }));
  };

  const handleFieldChange = (field, value) => {
    setEditingPremise((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!editingPremise) return;

    setIsSaving(true);
    try {
      const updatedData = {
        premise_label: editingPremise.premise_label,
        premise_description: editingPremise.premise_description,
        is_setupkey_generated: editingPremise.is_setupkey_generated,
        is_active: editingPremise.is_active,
      };

      await updateScenarioPremise(editingPremise.premise_key, updatedData);

      setPremises((prev) =>
        prev.map((p) =>
          p.premise_key === editingPremise.premise_key
            ? { ...p, ...updatedData }
            : p,
        ),
      );

      toast.success(t("premise_updated_success"));
      setExpandedPremise(null);
      setEditingPremise(null);
    } catch (error) {
      console.error("Error saving premise:", error);
      toast.error(t("premise_update_error"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setExpandedPremise(null);
    setEditingPremise(null);
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      {/* HEADER */}
      <Box className={classes.dialogHeader}>
        <Typography className={classes.headerTitle}>
          {t("manage_premises")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <Box className={classes.dialogContent}>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
          >
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color={userDetails?.implementation_color || "#1976d2"}
              ariaLabel="loading"
            />
          </Box>
        ) : (
          premises.map((premise) => {
            const isExpanded = expandedPremise === premise.premise_key;
            const isDescriptionExpanded =
              expandedDescription[premise.premise_key];

            return (
              <Box key={premise.premise_key} className={classes.premiseCard}>
                {/* CARD HEADER */}
                <Box className={classes.premiseHeader}>
                  <Box className={classes.premiseHeaderLeft}>
                    <Chip
                      label={premise.premise_order}
                      size="small"
                      className={classes.orderChip}
                    />
                    <Box className={classes.premiseNameKeyGroup}>
                      <Typography className={classes.premiseTitle}>
                        {premise.premise_label}
                      </Typography>
                      <Chip
                        label={premise.premise_key}
                        size="small"
                        className={classes.metadataChip}
                      />
                    </Box>
                    <Box className={classes.premiseDescriptionContainer}>
                      <Box
                        className={`${classes.premiseDescription} ${
                          isDescriptionExpanded
                            ? classes.premiseDescriptionExpanded
                            : ""
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: premise.premise_description || "",
                        }}
                      />
                      <IconButton
                        size="small"
                        className={classes.premiseDescriptionToggle}
                        onClick={() =>
                          handleToggleDescription(premise.premise_key)
                        }
                      >
                        {isDescriptionExpanded ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </Box>
                    <Box className={classes.premiseMetadata}>
                      {premise.is_setupkey_generated ? (
                        <Chip
                          label={t("setupkey_enabled")}
                          size="small"
                          icon={<CheckCircleIcon />}
                          className={classes.setupKeyChipEnabled}
                        />
                      ) : (
                        <Chip
                          label={t("setupkey_disabled")}
                          size="small"
                          icon={<CancelIcon />}
                          className={classes.setupKeyChipDisabled}
                        />
                      )}
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(premise)}
                    disabled={isExpanded}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* CARD BODY (COLLAPSIBLE) */}
                <Collapse in={isExpanded}>
                  {editingPremise && isExpanded && (
                    <Box className={classes.premiseBody}>
                      {/* ALL INLINE FIELDS IN ONE ROW */}
                      <Box className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel}>
                          {t("order")}
                        </Typography>
                        <Box className={classes.readOnlyField}>
                          {editingPremise.premise_order}
                        </Box>

                        <Typography
                          className={classes.fieldLabel}
                          style={{ marginLeft: "16px" }}
                        >
                          {t("key")}
                        </Typography>
                        <Box className={classes.readOnlyField}>
                          {editingPremise.premise_key}
                        </Box>

                        <Typography
                          className={classes.fieldLabel}
                          style={{ marginLeft: "16px" }}
                        >
                          {t("label")}
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={editingPremise.premise_label}
                          onChange={(e) =>
                            handleFieldChange("premise_label", e.target.value)
                          }
                        />
                      </Box>

                      {/* SWITCHES */}
                      <Box className={classes.switchRow}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editingPremise.is_setupkey_generated}
                              onChange={(e) =>
                                handleFieldChange(
                                  "is_setupkey_generated",
                                  e.target.checked,
                                )
                              }
                              color="primary"
                            />
                          }
                          label={t("is_setupkey_generated_label")}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editingPremise.is_active}
                              onChange={(e) =>
                                handleFieldChange("is_active", e.target.checked)
                              }
                              color="primary"
                            />
                          }
                          label={t("is_active_label")}
                        />
                      </Box>

                      {/* DESCRIPTION - FULL WIDTH */}
                      <Box className={classes.descriptionRow}>
                        <Typography className={classes.fieldLabel}>
                          {t("premise_description")}
                        </Typography>
                        <TextEditor
                          key={`editor-${editingPremise.premise_key}`}
                          initialContent={editingPremise.premise_description}
                          readOnly={false}
                          setContent={(html) =>
                            handleFieldChange("premise_description", html)
                          }
                        />
                      </Box>

                      {/* ACTION BUTTONS */}
                      <Box className={classes.actionButtons}>
                        <Button
                          variant="outlined"
                          onClick={handleCancel}
                          disabled={isSaving}
                          className={classes.cancelButton}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSave}
                          disabled={isSaving}
                          className={classes.saveButton}
                        >
                          {t("save_changes")}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
    </Dialog>
  );
}

DialogPremises.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
